import React from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import {
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  OutlinedInput,
  colors,
} from '@mui/material';

function SelectField(props) {
  const {
    name,
    label,
    data = [],
    disabled,
    multiple = false,
    onChange,
    required,
    requiredHintText = 'This field is required',
    sx,
    ...rest
  } = props;
  const [field, meta] = useField(name);
  const { value: selectedValue } = field;
  const { touched, error } = meta;
  const isError = touched && error;

  const isValueEmpty = (value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (value == null) {
      return true;
    }
    if (typeof value === 'string') {
      return value.trim() === '';
    }
    return false;
  };

  function renderHelperText() {
    if (isError) {
      return <FormHelperText>{error}</FormHelperText>;
    }
    if (required && isValueEmpty(selectedValue)) {
      return <FormHelperText>{requiredHintText}</FormHelperText>;
    }
    return null;
  }

  return (
    <FormControl
      {...rest}
      error={Boolean(isError)}
      sx={[
        { background: disabled ? colors.grey[200] : 'inherit' },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        {...field}
        input={<OutlinedInput label={label} />}
        disabled={disabled}
        value={selectedValue === undefined ? '' : selectedValue}
        {...(onChange ? { onChange } : {})} // To override onChange in components
        multiple={multiple}
      >
        {data?.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        ))}
      </Select>
      {renderHelperText()}
    </FormControl>
  );
}

SelectField.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  data: PropTypes.array.isRequired,
  required: PropTypes.bool,
  requiredHintText: PropTypes.string,
  sx: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.func,
    PropTypes.object
  ])
};

export default SelectField;
